import { XMLParser } from 'fast-xml-parser';
import type { NewsItem } from '@/types';
import { RSS_FEEDS, type FeedConfig, type FeedCategory, type FeedRegion } from '@/config/data-sources';
import { hashString } from '@/utils/format';
import { stripHtml, sanitizeUrl } from '@/utils/sanitize';

// ---------------------------------------------------------------------------
// RSS / Atom Feed Fetcher
// Uses a CORS proxy for browser-based fetching.
// Handles 75+ feeds via batched parallel fetching.
// ---------------------------------------------------------------------------

const TIMEOUT_MS = 5_000;        // aggressive timeout — fail fast
const MAX_ITEMS_PER_FEED = 10;
const BATCH_SIZE = 12;           // larger batches = fewer sequential rounds
const BATCH_DELAY_MS = 80;       // minimal inter-batch delay
const DEFAULT_MAX_FEEDS = 40;    // covers tier-1 + key tier-2 mideast/regional

// Dev: Vite 미들웨어 /api/rss, Prod: Vercel 서버리스 /api/rss — 동일 경로
const corsProxy = (url: string) => `/api/rss?url=${encodeURIComponent(url)}`;

const xmlParser = new XMLParser({
  ignoreAttributes: false,
  attributeNamePrefix: '@_',
  textNodeName: '#text',
  isArray: (name) => ['item', 'entry'].includes(name),
  // Prevent parser crash on complex CDATA/nested HTML inside feed content
  stopNodes: ['*.description', '*.content', '*.content\\:encoded', '*.summary'],
  parseTagValue: false,
});

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Fetch and parse a single RSS/Atom feed.
 * Goes straight to CORS proxy in browser (direct fetch always fails due to CORS).
 */
export async function fetchRssFeed(
  feedUrl: string,
  sourceName: string,
  feedCategory?: FeedCategory
): Promise<NewsItem[]> {
  // In browser, skip direct fetch — go straight to proxy
  const url = typeof window !== 'undefined' ? corsProxy(feedUrl) : feedUrl;

  try {
    return await fetchAndParse(url, sourceName, feedCategory);
  } catch {
    // Silent fail — feed simply unavailable
    return [];
  }
}

/** Options for filtering which feeds to fetch */
export interface FetchFeedsOptions {
  /** Only fetch feeds matching these categories */
  categories?: FeedCategory[];
  /** Only fetch feeds matching these regions */
  regions?: FeedRegion[];
  /** Maximum number of feeds to fetch (default: all matching) */
  maxFeeds?: number;
  /** Only fetch specific tier(s) */
  tiers?: (1 | 2 | 3)[];
  /** Language filter */
  lang?: string;
}

/**
 * Fetch all (or filtered) feeds in batched parallel, deduplicate by title similarity.
 * Sorts by tier priority, fetches in batches of BATCH_SIZE to avoid overloading proxies.
 */
export async function fetchAllFeeds(options?: FetchFeedsOptions): Promise<NewsItem[]> {
  let feeds: FeedConfig[] = [...RSS_FEEDS];

  // Apply filters
  if (options?.categories?.length) {
    const cats = new Set(options.categories);
    feeds = feeds.filter((f) => cats.has(f.category));
  }
  if (options?.regions?.length) {
    const regs = new Set(options.regions);
    feeds = feeds.filter((f) => regs.has(f.region));
  }
  if (options?.tiers?.length) {
    const ts = new Set(options.tiers);
    feeds = feeds.filter((f) => ts.has(f.tier));
  }
  if (options?.lang) {
    feeds = feeds.filter((f) => (f.lang ?? 'en') === options.lang);
  }

  const hasAnyFilter = !!(options?.categories?.length || options?.regions?.length || options?.tiers?.length || options?.lang);

  // Sort by tier (lower = higher priority)
  feeds.sort((a, b) => a.tier - b.tier);

  // Cap at requested limit, or DEFAULT_MAX_FEEDS if no filters applied
  const limit = options?.maxFeeds ?? (hasAnyFilter ? feeds.length : DEFAULT_MAX_FEEDS);
  if (limit < feeds.length) {
    // defense 피드는 컷과 무관하게 항상 포함 (Event Timeline military 탭 소스)
    const sliced = feeds.slice(0, limit);
    const included = new Set(sliced.map((f) => f.url));
    const defense = feeds.filter((f) => f.category === 'defense' && !included.has(f.url));
    feeds = [...sliced, ...defense];
  }

  // Batched parallel fetch
  const allItems: NewsItem[] = [];
  for (let i = 0; i < feeds.length; i += BATCH_SIZE) {
    const batch = feeds.slice(i, i + BATCH_SIZE);
    const results = await Promise.allSettled(
      batch.map((feed) => fetchRssFeed(feed.url, feed.name, feed.category))
    );
    for (const result of results) {
      if (result.status === 'fulfilled') {
        allItems.push(...result.value);
      }
    }
    // Small delay between batches (skip after last batch)
    if (i + BATCH_SIZE < feeds.length) {
      await delay(BATCH_DELAY_MS);
    }
  }

  // Sort by date descending
  allItems.sort(
    (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
  );

  // Deduplicate by title similarity
  return deduplicateByTitle(allItems);
}

/** Convenience: fetch only Asia-region feeds */
export function fetchAsiaFeeds(): Promise<NewsItem[]> {
  return fetchAllFeeds({ regions: ['asia'] });
}

/** Convenience: fetch only Korean-language feeds */
export function fetchKoreanFeeds(): Promise<NewsItem[]> {
  return fetchAllFeeds({ categories: ['korea'], lang: 'ko' });
}

/** Convenience: fetch defense & security feeds */
export function fetchDefenseFeeds(): Promise<NewsItem[]> {
  return fetchAllFeeds({ categories: ['defense'] });
}

/** Convenience: fetch finance & energy feeds */
export function fetchMarketNewsFeeds(): Promise<NewsItem[]> {
  return fetchAllFeeds({ categories: ['finance', 'energy'] });
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function fetchAndParse(url: string, sourceName: string, feedCategory?: FeedCategory): Promise<NewsItem[]> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const res = await fetch(url, {
      signal: controller.signal,
      headers: {
        Accept: 'application/rss+xml, application/xml, application/atom+xml, text/xml, */*',
      },
    });

    if (!res.ok) {
      throw new Error(`RSS fetch returned ${res.status} for ${sourceName}`);
    }

    const text = await res.text();
    if (!text.trim()) {
      throw new Error(`Empty response from ${sourceName}`);
    }

    return parseXml(text, sourceName, feedCategory);
  } finally {
    clearTimeout(timer);
  }
}

function parseXml(xml: string, sourceName: string, feedCategory?: FeedCategory): NewsItem[] {
  try {
    const parsed = xmlParser.parse(xml);

    // RSS 2.0 format
    const rssItems = parsed?.rss?.channel?.item;
    if (rssItems) {
      return parseRssItems(rssItems, sourceName, feedCategory);
    }

    // Atom format
    const atomEntries = parsed?.feed?.entry;
    if (atomEntries) {
      return parseAtomEntries(atomEntries, sourceName, feedCategory);
    }

    // RDF / RSS 1.0 format
    const rdfItems = parsed?.['rdf:RDF']?.item;
    if (rdfItems) {
      return parseRssItems(rdfItems, sourceName, feedCategory);
    }

    console.warn(`[RSS] Unknown feed format from ${sourceName}`);
    return [];
  } catch (err) {
    console.warn(`[RSS] XML parse error for ${sourceName}`, err);
    return [];
  }
}

function parseRssItems(items: unknown[], sourceName: string, feedCategory?: FeedCategory): NewsItem[] {
  if (!Array.isArray(items)) return [];

  return items
    .slice(0, MAX_ITEMS_PER_FEED)
    .map((raw): NewsItem | null => {
      const item = raw as Record<string, unknown>;
      const title = extractText(item.title);
      if (!title) return null;

      const link = extractLink(item.link) ?? extractText(item.guid);
      const description = extractText(item.description);
      const pubDate = extractText(item.pubDate) ?? extractText(item['dc:date']);

      // Look for media/enclosure images
      const imageUrl = extractImage(item);

      const url = link ? sanitizeUrl(link) : null;

      return {
        id: `rss-${hashString(url ?? title)}`,
        title: stripHtml(title).slice(0, 200),
        description: description ? stripHtml(description).slice(0, 500) : undefined,
        source: sourceName,
        url: url ?? '',
        publishedAt: pubDate ? safeISODate(pubDate) : new Date().toISOString(),
        imageUrl: imageUrl ?? undefined,
        category: 'news',
        feedCategory,
      };
    })
    .filter((item): item is NewsItem => item !== null && item.url !== '');
}

function parseAtomEntries(entries: unknown[], sourceName: string, feedCategory?: FeedCategory): NewsItem[] {
  if (!Array.isArray(entries)) return [];

  return entries
    .slice(0, MAX_ITEMS_PER_FEED)
    .map((raw): NewsItem | null => {
      const entry = raw as Record<string, unknown>;
      const title = extractText(entry.title);
      if (!title) return null;

      const link = extractAtomLink(entry.link);
      const summary = extractText(entry.summary) ?? extractText(entry.content);
      const updated = extractText(entry.updated) ?? extractText(entry.published);

      const url = link ? sanitizeUrl(link) : null;

      return {
        id: `rss-${hashString(url ?? title)}`,
        title: stripHtml(title).slice(0, 200),
        description: summary ? stripHtml(summary).slice(0, 500) : undefined,
        source: sourceName,
        url: url ?? '',
        publishedAt: updated ? safeISODate(updated) : new Date().toISOString(),
        category: 'news',
        feedCategory,
      };
    })
    .filter((item): item is NewsItem => item !== null && item.url !== '');
}

// ---------------------------------------------------------------------------
// XML value extraction helpers
// ---------------------------------------------------------------------------

function extractText(node: unknown): string | null {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (node && typeof node === 'object') {
    const obj = node as Record<string, unknown>;
    // Handle { '#text': '...' } or { '@_type': 'html', '#text': '...' }
    if ('#text' in obj) return String(obj['#text']);
    // CDATA content
    if ('__cdata' in obj) return String(obj['__cdata']);
  }
  return null;
}

function extractLink(node: unknown): string | null {
  if (typeof node === 'string') return node;
  if (node && typeof node === 'object') {
    const obj = node as Record<string, unknown>;
    if ('@_href' in obj) return String(obj['@_href']);
    if ('#text' in obj) return String(obj['#text']);
  }
  return null;
}

function extractAtomLink(node: unknown): string | null {
  if (typeof node === 'string') return node;
  if (Array.isArray(node)) {
    // Prefer 'alternate' link type
    const alt = node.find(
      (l: Record<string, unknown>) =>
        l['@_rel'] === 'alternate' || !l['@_rel']
    );
    const target = alt ?? node[0];
    return target?.['@_href'] ? String(target['@_href']) : null;
  }
  if (node && typeof node === 'object') {
    const obj = node as Record<string, unknown>;
    if ('@_href' in obj) return String(obj['@_href']);
  }
  return null;
}

function extractImage(item: Record<string, unknown>): string | null {
  // media:content
  const media = item['media:content'] as Record<string, unknown> | undefined;
  if (media?.['@_url']) return String(media['@_url']);

  // media:thumbnail
  const thumb = item['media:thumbnail'] as Record<string, unknown> | undefined;
  if (thumb?.['@_url']) return String(thumb['@_url']);

  // enclosure
  const enclosure = item.enclosure as Record<string, unknown> | undefined;
  if (enclosure?.['@_url'] && String(enclosure['@_type'] ?? '').startsWith('image/')) {
    return String(enclosure['@_url']);
  }

  return null;
}

function safeISODate(raw: string): string {
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return new Date().toISOString();
    return d.toISOString();
  } catch {
    return new Date().toISOString();
  }
}

// ---------------------------------------------------------------------------
// Title-similarity deduplication
// ---------------------------------------------------------------------------

/**
 * Deduplicate articles whose normalized titles are > 70% similar.
 * Uses a simple Jaccard-like token overlap.
 */
function deduplicateByTitle(items: NewsItem[]): NewsItem[] {
  const seen: string[][] = []; // array of token sets already accepted
  const result: NewsItem[] = [];

  for (const item of items) {
    const tokens = normalize(item.title);
    if (tokens.length === 0) continue;

    const isDuplicate = seen.some((prev) => jaccardSimilarity(prev, tokens) > 0.7);
    if (!isDuplicate) {
      seen.push(tokens);
      result.push(item);
    }
  }

  return result;
}

function normalize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .split(/\s+/)
    .filter((w) => w.length > 2); // drop short stopwords
}

function jaccardSimilarity(a: string[], b: string[]): number {
  const setA = new Set(a);
  const setB = new Set(b);
  let intersection = 0;
  for (const token of setA) {
    if (setB.has(token)) intersection++;
  }
  const union = setA.size + setB.size - intersection;
  return union === 0 ? 0 : intersection / union;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
